import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, imageUrl, assetUrl } from "@/lib/api";
import { ArrowLeft, UserCircle, ArrowSquareOut, FolderSimple, MapPin, Images, Buildings, Globe } from "@phosphor-icons/react";

export default function PhotographerDetail() {
  const { name } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(undefined);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get(`/photographers/${encodeURIComponent(name)}`).then((r) => setData(r.data)).catch(() => setData(null));
    api.get(`/profiles/${encodeURIComponent(name)}`).then((r) => setProfile(r.data)).catch(() => setProfile(null));
  }, [name]);

  if (data === undefined) return <div className="grid h-64 place-items-center text-muted-foreground">Loading…</div>;
  if (data === null) return (
    <div className="mx-auto max-w-3xl">
      <button onClick={() => nav("/photographers")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-zinc-900"><ArrowLeft size={16} weight="bold" />Photographers</button>
      <p className="text-muted-foreground">Photographer not found.</p>
    </div>
  );

  return (
    <div className="mx-auto max-w-[1400px]">
      <button onClick={() => nav("/photographers")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-zinc-900">
        <ArrowLeft size={16} weight="bold" /> Photographers
      </button>

      {profile && (profile.account_type === "business" || profile.bio || profile.logo_url) ? (
        <div data-testid="business-profile-header" className="mb-6 overflow-hidden rounded-xl border border-border bg-white">
          <div className="h-20 bg-gradient-to-r from-[#F27A54] to-[#0057ff]" />
          <div className="px-6 pb-6">
            <div className="-mt-10 flex flex-wrap items-end gap-4">
              <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-xl border-4 border-white bg-zinc-100 shadow">
                {profile.logo_url || profile.avatar_url
                  ? <img src={assetUrl(profile.logo_url) || profile.avatar_url} alt="" className="h-full w-full object-cover" />
                  : <Buildings size={32} className="text-[#F27A54]" weight="fill" />}
              </div>
              <div className="min-w-0 flex-1 pb-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-2xl font-black tracking-tight">{profile.business_name || profile.photographer_name || data.photographer}</h1>
                  {profile.account_type === "business" && (
                    <span className="flex items-center gap-1 rounded-full bg-[#F27A54] px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                      <Buildings size={11} weight="fill" /> Business
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{data.total} photos · {data.countries.length} region folder(s)</p>
              </div>
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noreferrer" data-testid="business-website-link"
                  className="flex items-center gap-1.5 rounded-md border border-[#F27A54]/30 bg-blue-50 px-3 py-2 text-sm font-semibold text-[#F27A54] hover:bg-blue-100">
                  <Globe size={15} weight="bold" /> Visit website
                </a>
              )}
            </div>
            {profile.bio && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-600">{profile.bio}</p>}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-[#F27A54]/10 text-[#F27A54]"><UserCircle size={30} weight="fill" /></div>
            <div>
              <h1 className="font-display text-3xl font-black tracking-tight">{data.photographer}</h1>
              <p className="text-sm text-muted-foreground">{data.total} photos · {data.countries.length} region folder(s)</p>
            </div>
          </div>
          {data.photographer_url && (
            <a href={data.photographer_url} target="_blank" rel="noreferrer"
              data-testid="photographer-profile-link"
              className="flex items-center gap-1.5 rounded-md border border-[#F27A54]/30 bg-blue-50 px-3 py-2 text-sm font-semibold text-[#F27A54] hover:bg-blue-100">
              <ArrowSquareOut size={15} weight="bold" /> View Pulse profile
            </a>
          )}
        </div>
      )}

      <div className="mt-8 space-y-10">
        {data.countries.map((country) => (
          <section key={country.country}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <FolderSimple size={20} weight="fill" className="text-[#F27A54]" />
              <h2 className="font-display text-xl font-bold tracking-tight">{country.country}</h2>
              <span className="font-mono-p text-sm text-muted-foreground">{country.count}</span>
            </div>
            <div className="mt-4 space-y-6">
              {country.cities.map((city) => (
                <div key={city.city}>
                  <h3 className="flex items-center gap-1.5 text-sm font-bold text-zinc-700">
                    <MapPin size={14} weight="fill" className="text-[#F27A54]" />{city.city}
                    <span className="font-mono-p text-xs font-normal text-muted-foreground">· {city.count}</span>
                  </h3>
                  <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                    {city.photo_ids.map((pid) => (
                      <Link key={pid} to={`/photos/${pid}`} className="overflow-hidden rounded-md border border-border">
                        <img src={imageUrl(pid)} alt="" className="aspect-square w-full object-cover transition-transform duration-300 hover:scale-105" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
